import type { FC, ReactNode } from 'react'
import type { Category, Product } from '@/types'

import { useEffect, useMemo, useRef, useState } from 'react'
import { Box, Search, X } from 'lucide-react'
import { cn } from '@/lib/utils'
import { formatIDR } from '@/lib/format'
import { ProductImage } from '@/components/shop/product-image'

interface BoutiqueCatalogProps {
    open: boolean
    products: Array<Product>
    categories: Array<Category>
    // The category of the shelf the camera is looking at, used as the starting filter.
    shelfCategory: string | null
    // Product ids that stand on a shelf as a 3D doll.
    onDisplay: Set<string>
    onPick: (product: Product) => void
    onClose: () => void
}

// The interactive catalogue inside the 3D boutique. Every active product is here, including the ones
// that have photos but no 3D model, so everything the shop sells can be browsed without leaving.
export const BoutiqueCatalog: FC<BoutiqueCatalogProps> = ({ open, products, categories, shelfCategory, onDisplay, onPick, onClose }): ReactNode => {
    const [category, setCategory] = useState<string | null>(shelfCategory)
    const [query, setQuery] = useState('')
    const closeButton = useRef<HTMLButtonElement>(null)

    useEffect(() => {
        if (!open) return

        setCategory(shelfCategory)
        closeButton.current?.focus({ preventScroll: true })
    }, [open, shelfCategory])

    const visible = useMemo(() => {
        const terms = query.toLowerCase().split(/\s+/).filter(Boolean)

        return products.filter(product => {
            if (category && product.category?.slug !== category) return false
            if (!terms.length) return true

            const text = `${product.name} ${product.category?.name ?? ''} ${product.tagline ?? ''}`.toLowerCase()

            return terms.every(term => text.includes(term))
        })
    }, [products, category, query])

    const used = categories.filter(entry => products.some(product => product.category?.slug === entry.slug))

    return (
        <aside
            aria-label='Katalog butik'
            inert={!open}
            className={cn(
                'absolute z-30 flex flex-col overflow-hidden border border-white/60 bg-cream-50/94 shadow-[0_30px_80px_-30px_rgba(58,40,30,0.55)] backdrop-blur-xl transition-all duration-500 ease-[cubic-bezier(0.22,1,0.36,1)]',
                'inset-x-2 bottom-2 top-[30svh] rounded-[28px]',
                'lg:inset-x-auto lg:bottom-6 lg:right-6 lg:top-24 lg:w-[420px]',
                open ? 'translate-y-0 opacity-100 lg:translate-x-0' : 'pointer-events-none translate-y-[110%] opacity-0 lg:translate-x-[115%] lg:translate-y-0'
            )}
        >
            <div className='flex items-center justify-between gap-3 border-b border-border/70 px-5 py-3.5'>
                <div>
                    <h2 className='font-heading text-xl text-cocoa-900'>
                        Katalog butik
                    </h2>
                    <p className='text-xs text-muted-foreground'>
                        <span>{visible.length}</span> produk
                    </p>
                </div>
                <button
                    ref={closeButton}
                    type='button'
                    onClick={onClose}
                    aria-label='Tutup katalog'
                    className='grid size-9 place-items-center rounded-full bg-white text-cocoa-700 shadow-sm transition hover:bg-blush-50'
                >
                    <X className='size-5' />
                </button>
            </div>

            <div className='space-y-3 border-b border-border/70 px-5 py-3'>
                <label className='relative block'>
                    <span className='sr-only'>Cari Produk</span>
                    <Search className='pointer-events-none absolute left-3.5 top-1/2 size-4 -translate-y-1/2 text-cocoa-500' />
                    <input
                        type='search'
                        value={query}
                        onChange={event => setQuery(event.target.value)}
                        placeholder='Cari Produk'
                        className='h-10 w-full rounded-full border border-input bg-white pl-10 pr-4 text-sm outline-none transition focus:border-caramel-400 focus:ring-2 focus:ring-caramel-300/40'
                    />
                </label>
                <div className='no-scrollbar -mx-5 flex gap-1.5 overflow-x-auto px-5'>
                    <button
                        type='button'
                        onClick={() => setCategory(null)}
                        aria-pressed={!category}
                        className={cn('shrink-0 rounded-full px-3 py-1.5 text-xs font-semibold transition', !category ? 'bg-cocoa-800 text-cream-50' : 'bg-white text-cocoa-700 hover:bg-blush-50')}
                    >
                        Semua
                    </button>
                    {used.map(entry => (
                        <button
                            key={entry.id}
                            type='button'
                            onClick={() => setCategory(entry.slug)}
                            aria-pressed={category === entry.slug}
                            className={cn('shrink-0 rounded-full px-3 py-1.5 text-xs font-semibold transition', category === entry.slug ? 'bg-cocoa-800 text-cream-50' : 'bg-white text-cocoa-700 hover:bg-blush-50')}
                        >
                            <span data-mdlfy-cms={`categories:${entry.id}:name`}>{entry.name}</span>
                        </button>
                    ))}
                </div>
            </div>

            <div className='flex-1 overflow-y-auto overscroll-contain px-4 py-4'>
                {visible.length === 0
                    ? (
                        <p className='px-2 py-10 text-center text-sm text-muted-foreground'>
                            Belum ada produk yang cocok di sini.
                        </p>
                    )
                    : (
                        <ul className='grid grid-cols-2 gap-3'>
                            {visible.map(product => {
                                const soldOut = !product.madeToOrder && product.stock <= 0

                                return (
                                    <li key={product.id}>
                                        <button
                                            type='button'
                                            onClick={() => onPick(product)}
                                            className='group flex w-full flex-col overflow-hidden rounded-2xl bg-white text-left shadow-[0_10px_24px_-18px_rgba(58,40,30,0.6)] transition hover:-translate-y-0.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-caramel-400'
                                        >
                                            <span className='relative block'>
                                                <ProductImage
                                                    src={product.image}
                                                    alt={product.imageAlt ?? product.name}
                                                    designKey={product.designKey}
                                                    mainColor={product.colors[0]?.hex ?? null}
                                                    accentColor={product.accentColor}
                                                    className='aspect-square w-full'
                                                    imageClassName='transition-transform duration-500 group-hover:scale-105'
                                                />
                                                {onDisplay.has(product.id) && (
                                                    <span className='absolute left-2 top-2 inline-flex items-center gap-1 rounded-full bg-cream-50/90 px-2 py-0.5 text-[10px] font-bold text-cocoa-800 backdrop-blur'>
                                                        <Box className='size-3' />
                                                        Di rak
                                                    </span>
                                                )}
                                                {soldOut && (
                                                    <span className='absolute bottom-2 left-2 rounded-full bg-cocoa-800/85 px-2 py-0.5 text-[10px] font-bold text-cream-50'>
                                                        Stok Habis
                                                    </span>
                                                )}
                                            </span>
                                            <span className='block px-3 py-2.5'>
                                                <span className='block truncate font-heading text-[15px] text-cocoa-900'>
                                                    <span data-mdlfy-cms={`products:${product.id}:name`}>{product.name}</span>
                                                </span>
                                                <span className='text-xs font-bold text-cocoa-600'>
                                                    {formatIDR(product.price)}
                                                </span>
                                            </span>
                                        </button>
                                    </li>
                                )
                            })}
                        </ul>
                    )}
            </div>
        </aside>
    )
}
